import React, { useMemo, useState } from "react";
import * as R from "ramda";
import { Checkbox, Modal, Radio, Select } from "antd";

import Button from "../../../../../components/Button";
import { DropdownContainer } from "../../../../../components/inputs/SelectWithIcons";
import TestOption from "../../../../../components/inputs/TestOption";

import styled from "styled-components/macro";

const { Option, OptGroup } = Select;

const MINIMUM_RESULT_OPTIONS = ["high", "medium", "low"];

const StyledModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 0;
    overflow: hidden;
  }

  .ant-modal-close-x {
    color: ${({ theme }) => theme["@gray-60"]};
    font-size: 16px;
    line-height: 62px;
  }

  .ant-modal-header {
    border-bottom: 0;
    padding: 36px 44px 18px;
  }

  .ant-modal-title {
    color: ${({ theme }) => theme["@gray-80"]};
    font-size: 22px;
    font-weight: 800;
    line-height: 1.2;
  }

  .ant-modal-body {
    padding: 0;
  }
  .ant-radio-group {
    display: flex !important;
  }
`;

const EvaluationSelectorSection = styled.div`
  padding: 0 44px 40px;
`;

const KnockoutSection = styled.div`
  border-top: 1px solid ${({ theme }) => theme["@gray-30"]};
  padding: 24px 44px 46px;

  .ant-checkbox-wrapper {
    align-items: center;
    display: flex;
    font-size: 18px;
    color: ${({ theme }) => theme["@gray-80"]};
    font-weight: 800;
    line-height: 1.25;
  }

  .ant-checkbox {
    top: 0;
  }

  .ant-checkbox-inner {
    border-radius: 2px;
    height: 18px;
    width: 18px;
  }

  .ant-checkbox-checked .ant-checkbox-inner {
    background-color: ${({ theme }) => theme["@primary"]};
    border-color: ${({ theme }) => theme["@primary"]};
  }
`;

const SectionTitle = styled.div`
  color: rgba(0, 0, 0, 0.85);
  font-size: 16px;
  font-weight: 800;
  margin: 44px 0 16px;
`;

const Description = styled.p`
  color: ${({ theme }) => theme["@gray-80"]};
  font-size: 14px;
  line-height: 1.45;
  margin: 14px 0 0;
  max-width: 690px;
`;

const MeetsRequirementsTag = styled.span`
  border: 1px solid ${({ theme }) => theme["@gray-40"]};
  border-radius: 12px;
  color: ${({ theme }) => theme["@gray-60"]};
  display: inline-block;
  font-size: 10px;
  font-weight: 800;
  line-height: 1;
  margin-left: 6px;
  padding: 4px 12px 3px;
  text-transform: uppercase;
`;

const ResultsGrid = styled(Radio.Group)`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  width: 100%;
`;

const ResultOption = styled(Radio)`
  align-items: center;
  border: 1px solid ${({ theme }) => theme["@gray-30"]};
  border-radius: 4px;
  color: ${({ theme, checked }) =>
    checked ? theme["@white"] : theme["@gray-80"]};
  display: flex !important;
  flex: 1;
  height: 58px;
  margin-right: 0 !important;
  padding: 0 16px !important;
  transition: all 0.2s ease;

  ${({ checked, theme }) =>
    checked &&
    `
      background-color: ${theme["@primary"]};
      border-color: ${theme["@primary"]};
    `}

  &.ant-radio-wrapper-checked {
    color: ${({ theme }) => theme["@white"]} !important;
  }

  &.ant-radio-wrapper-checked > span:last-child {
    color: ${({ theme }) => theme["@white"]} !important;
  }

  .ant-radio {
    margin-right: 10px;
  }

  .ant-radio-inner {
    height: 22px;
    width: 22px;
  }

  .ant-radio-checked .ant-radio-inner {
    background-color: ${({ theme }) => theme["@white"]};
    border-color: ${({ theme }) => theme["@white"]};
  }

  .ant-radio-checked .ant-radio-inner::after {
    background-color: ${({ theme }) => theme["@primary"]};
    top: 6px;
    left: 6px;
  }

  span {
    color: inherit;
  }
`;

const StyledSelect = styled(Select)`
  width: 100%;

  .ant-select-selection {
    align-items: center;
    border: 2px solid ${({ theme }) => theme["@primary"]};
    border-radius: 4px;
    box-shadow: none !important;
    display: flex;
    min-height: 80px;
  }

  .ant-select-selection__rendered {
    flex: 1;
    line-height: normal;
    margin-left: 0;
    margin-right: 44px;
  }

  .ant-select-selection__placeholder {
    margin-left: 10px;
  }

  .ant-select-selection-selected-value {
    align-items: center;
    display: flex !important;
    float: none;
    min-height: 76px;
    padding-left: 18px;
    margin-top: 13px;
    width: 100%;
  }

  .ant-select-selection-selected-value .wrapper {
    display: flex;
    flex-direction: column;
  }

  .ant-select-selection-selected-value .header {
    margin-bottom: 4px;
  }

  .ant-select-selection-selected-value .anticon {
    width: 30px;
    height: 30px;
    font-size: 16px;
    margin-right: 12px;
  }

  .ant-select-selection-selected-value .title {
    color: ${({ theme }) => theme["@primary"]};
    font-weight: 800;
  }

  .ant-select-selection-selected-value .description {
    color: ${({ theme }) => theme["@gray-80"]};
    display: block !important;
    font-size: 13px;
    line-height: 1.35;
  }

  .ant-select-arrow {
    color: ${({ theme }) => theme["@gray-60"]};
    font-size: 18px;
    right: 22px;
  }
`;

const Footer = styled.div`
  align-items: center;
  border-top: 1px solid ${({ theme }) => theme["@gray-30"]};
  display: flex;
  justify-content: space-between;
  padding: 28px 55px 32px;
`;

const FooterButton = styled(Button)`
  border-radius: 22px !important;
  height: 44px !important;
  min-width: 132px !important;
`;

const getSelectedTestValue = question => {
  const source = R.path(["config", "source"], question);
  const idTest = R.path(["config", "idTest"], question);

  return source && idTest ? `${source}-${idTest}` : undefined;
};

const getMinimumResult = question =>
  R.path(["config", "minimumResult"], question) || "high";

const isSentinelTest = test =>
  test.source === "worcket" && String(test.idTest) === "8";

const renderTestOption = ({ key, source, idTest, name, cost }, t) => (
  <TestOption
    t={t}
    testKey={key}
    source={source}
    idTest={idTest}
    name={name}
    cost={cost > 0 && cost}
  />
);

const renderTestOptions = (tests, label, showLabel, t) => {
  const sortedTests = R.pipe(
    R.groupBy(R.prop("source")),
    R.values,
    R.map(R.sortWith([R.ascend(R.prop("idTest"))])),
    R.flatten
  )(tests);

  const options = sortedTests.map(test => (
    <Option
      key={`${test.source}-${test.idTest}`}
      label={renderTestOption(test, t)}
      value={`${test.source}-${test.idTest}`}
    >
      {renderTestOption(test, t)}
    </Option>
  ));

  return showLabel ? (
    <OptGroup key={label} label={label}>
      {options}
    </OptGroup>
  ) : (
    options
  );
};

export default function EvaluationModal({
  question,
  saveQuestion,
  onCancel,
  t,
  tests = []
}) {
  const selectableTests = useMemo(
    () =>
      tests.filter(
        test =>
          test.selectableInQuestionsCatalog !== false && !isSentinelTest(test)
      ),
    [tests]
  );

  const [selectedTestId, setSelectedTestId] = useState(
    getSelectedTestValue(question)
  );
  const [knockout, setKnockout] = useState(
    question ? Boolean(question.knockout) : true
  );
  const [minimumResult, setMinimumResult] = useState(
    getMinimumResult(question)
  );

  const evaluationTests = useMemo(
    () =>
      selectableTests.filter(
        test => test.source === "worcket" || test.validation !== true
      ),
    [selectableTests]
  );

  const selectedTest = useMemo(
    () =>
      evaluationTests.find(
        test => `${test.source}-${test.idTest}` === selectedTestId
      ),
    [evaluationTests, selectedTestId]
  );

  return (
    <StyledModal
      visible
      width={840}
      onCancel={onCancel}
      footer={null}
      title={t("opportunity:screening.evaluationModalTitle")}
    >
      <EvaluationSelectorSection>
        <StyledSelect
          showSearch
          value={selectedTestId}
          className="test-select"
          placeholder={t("opportunity:screening.evaluationPlaceholder")}
          optionFilterProp="children"
          optionLabelProp="label"
          onChange={value => setSelectedTestId(value)}
          dropdownRender={menu => <DropdownContainer>{menu}</DropdownContainer>}
        >
          {renderTestOptions(
            evaluationTests,
            t("opportunity:process.stepTypes.test.evaluationTests"),
            false,
            t
          )}
        </StyledSelect>
      </EvaluationSelectorSection>

      {selectedTest && (
        <KnockoutSection>
          <Checkbox
            checked={knockout}
            onChange={event => setKnockout(event.target.checked)}
          >
            {t("opportunity:screening.evaluationKnockout")}
          </Checkbox>
          <Description>
            {t("opportunity:screening.evaluationKnockoutDescription")}
            <MeetsRequirementsTag>
              {t("opportunity:screening.meetsRequirementsTag")}
            </MeetsRequirementsTag>
          </Description>

          {knockout && (
            <>
              <SectionTitle>
                {t("opportunity:screening.evaluationMinimumResult")}
              </SectionTitle>
              <ResultsGrid
                value={minimumResult}
                onChange={event => setMinimumResult(event.target.value)}
              >
                {MINIMUM_RESULT_OPTIONS.map(option => (
                  <ResultOption
                    checked={minimumResult === option}
                    key={option}
                    value={option}
                  >
                    {t(`opportunity:screening.resultLevels.${option}`)}
                  </ResultOption>
                ))}
              </ResultsGrid>
            </>
          )}
        </KnockoutSection>
      )}

      <Footer>
        <FooterButton size="lg" type="quaternary" onClick={onCancel}>
          {t("global:return")}
        </FooterButton>
        <FooterButton
          disabled={!selectedTest}
          size="lg"
          type="primary"
          onClick={() => {
            if (!selectedTest) return;

            const { source, idTest, key, name } = selectedTest;
            saveQuestion({
              id: question?.id || `test-${source}-${idTest}`,
              text:
                t([
                  `opportunity:process.stepTypes.test-${source}.${idTest}.title`,
                  name
                ]) || name,
              type: "test",
              knockout,
              enabled: question?.enabled !== false,
              config: {
                source,
                idTest,
                key,
                name,
                ...(knockout ? { minimumResult } : {})
              }
            });
            onCancel();
          }}
        >
          {t("global:save")}
        </FooterButton>
      </Footer>
    </StyledModal>
  );
}
